import { useState, useRef, useEffect, useCallback, Children } from 'react';

const Tabs = ({
  tabs = [],
  value,
  onChange,
  variant = 'underline',
  size = 'md',
  fullWidth = false,
  className = '',
  children,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const tabRefs = useRef([]);
  const containerRef = useRef(null);

  const activeTab = value !== undefined
    ? tabs.findIndex((t) => (typeof t === 'object' ? t.value : t) === value)
    : activeIndex;

  const updateIndicator = useCallback(() => {
    const tabEl = tabRefs.current[activeTab];
    if (!tabEl || variant !== 'underline') return;
    const container = containerRef.current;
    if (!container) return;
    const tabRect = tabEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    setIndicatorStyle({
      left: tabRect.left - containerRect.left,
      width: tabRect.width,
    });
  }, [activeTab, variant]);

  useEffect(() => {
    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [updateIndicator]);

  const handleSelect = (index) => {
    const tab = tabs[index];
    const tabValue = typeof tab === 'object' ? tab.value : tab;
    if (typeof tab === 'object' && tab.disabled) return;
    setActiveIndex(index);
    onChange?.(tabValue, index);
  };

  const sizeStyles = {
    sm: { tab: 'px-3 py-1.5 text-[11px] gap-1', icon: 11 },
    md: { tab: 'px-3.5 py-2 text-[12.5px] gap-1.5', icon: 13 },
    lg: { tab: 'px-4 py-2.5 text-[13px] gap-1.5', icon: 14 },
  };
  const s = sizeStyles[size];

  const normalizedTabs = tabs.map((t) =>
    typeof t === 'object' ? t : { label: String(t), value: t },
  );

  const variantContainerClass = {
    underline: 'border-b border-surface-200/60 relative',
    pills: 'bg-surface-100/60 rounded-[14px] p-1 gap-0.5',
    enclosed: 'border-b border-surface-200 gap-0',
  };

  const getTabClass = (index) => {
    const isActive = index === activeTab;
    const base = `${s.tab} font-medium transition-all duration-200 flex items-center whitespace-nowrap no-select`;

    if (variant === 'underline') {
      return `${base} rounded-t-lg border-b-2 -mb-px ${
        isActive
          ? 'border-primary-600 text-white'
          : 'border-transparent text-surface-600 hover:text-surface-700 hover:bg-surface-50/60 dark:hover:bg-surface-800'
      }`;
    }
    if (variant === 'pills') {
      return `${base} rounded-[10px] ${
        isActive
          ? 'text-white shadow-md'
          : 'text-surface-600 hover:text-surface-700'
      }`;
    }
    if (variant === 'enclosed') {
      return `${base} border border-transparent -mb-px ${
        isActive
          ? 'text-white border-primary-600 rounded-t-[10px] font-semibold'
          : 'text-surface-600 hover:text-surface-700'
      }`;
    }
    return base;
  };

  const getTabStyle = (index) => {
    const isActive = index === activeTab;
    if (isActive) return { background: 'linear-gradient(135deg, #2a2a72, #1338be)' };
    return {};
  };

  const panels = children ? Children.toArray(children) : null;

  return (
    <div className={`overflow-hidden ${className}`}>
      <div
        ref={containerRef}
        className={`flex ${variantContainerClass[variant]} overflow-x-auto max-w-full`}
        role="tablist"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {normalizedTabs.map((tab, i) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.value}
              ref={(el) => { tabRefs.current[i] = el; }}
              type="button"
              role="tab"
              aria-selected={i === activeTab}
              disabled={tab.disabled}
              onClick={() => handleSelect(i)}
              style={getTabStyle(i)}
              className={`
                ${getTabClass(i)} flex-shrink-0
                ${fullWidth ? 'flex-1 justify-center' : ''}
                ${tab.disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {Icon && <Icon size={s.icon} className="flex-shrink-0" />}
              <span>{tab.label}</span>
              {tab.badge !== undefined && (
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-2xs font-bold rounded-[6px]
                  ${i === activeTab ? 'text-white' : 'bg-surface-200/80 text-surface-600'}`}
                  style={i === activeTab ? { background: 'linear-gradient(135deg, #2a2a72, #1338be)' } : {}}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}

        {variant === 'underline' && (
          <div
            className="absolute bottom-0 h-[2.5px] rounded-full transition-all duration-250 ease-spring"
            style={{ ...indicatorStyle, background: 'linear-gradient(135deg, #2a2a72, #1338be)' }}
          />
        )}
      </div>

      {panels && (
        <div className="mt-4" role="tabpanel">
          {panels[activeTab]}
        </div>
      )}
    </div>
  );
};

Tabs.Panel = ({ children, className = '' }) => (
  <div className={`animate-fade-in ${className}`}>{children}</div>
);

export default Tabs;
